import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';
import {Modal, Tab, Tabs} from 'react-bootstrap';
import {ToastContainer} from 'react-toastify';
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent';
import {Column, Metric, Numeric, ReportTable, formatter, kg, tonnes} from './InventuraReportComponents';
import LokacijeRsTraka from '../../components/LokacijeRSTraka/LokacijeRsTraka';
import notifyToast from '../../components/Toast/Toast';
import {getSumaTrakaInventurePremaFmkSifri, izvjestajPremaLokacijiTrake,
    izvjestajZaInventuruTrakaV2, podesiNoveLokacijeRolni, fetchInventura,
    zakljuciInventuruTraka, ZakljuciInventuruTrakaPayload, INVENTURE_UPDATED_EVENT} from '../../api/inventure';
import {InventureModel} from '../../models/inventure';

interface ZadnjiPromet {
    zadnjiPrometId?: Numeric | null;
    uposlenik?: {id: Numeric; imePrezime: string | null} | null;
}
interface NeskeniranaTraka extends ZadnjiPromet {
    tezinaKg: Numeric;
    KreiranaTezina?: Numeric | null;
    Lokacija?: Numeric | null;
    Pakovanje?: Numeric | null;
}
interface Traka extends ZadnjiPromet {
    TrakaId: Numeric;
    Tezina: Numeric;
    SkeniranaTezina: Numeric;
    Duzina?: Numeric | null;
    SkeniranaDuzina?: Numeric | null;
    Lokacija?: string | null;
    SkeniranaLokacija?: string | null;
    SkeniranaLokacijaRed?: Numeric | null;
}
interface TrenutnaTraka extends Traka {
    Pakovanje?: Numeric | null;
    trenutnaTezinaKg?: Numeric | null;
    tezinaUkljucenaUTrenutnoStanjeKg: Numeric;
    razlikaKg: Numeric;
    razlog: string;
}
interface ReportData {
    skeniraneTrake: {brojTraka: Numeric; tezinaKg: Numeric};
    evidentiranaTezinaSkeniranihTrakaKg: Numeric;
    razlikaSkeniraneIEvidentiraneTezineKg: Numeric;
    brojNeskeniranihTraka: Numeric;
    tezinaNeskeniranihTrakaKg: Numeric;
    neskeniraneTrakeNaTrenutnomStanju: Record<string, NeskeniranaTraka | Numeric>;
    pronadjeneTrakeBezEvidentiraneTezine: Traka[];
    tezinaTrakaBezEvidentiraneTezineKg: Numeric;
    trakeSaOdstupanjemTezine: Traka[];
    brojTrakaSaOdstupanjemTezine: Numeric;
    razlikaTezineEvidentiranihTrakaKg: Numeric;
    trakeSaOdstupanjemLokacije: Traka[];
    brojTrakaSaOdstupanjemLokacije: Numeric;
    trakeSaOdstupanjemDuzine: Traka[];
    brojTrakaSaOdstupanjemDuzine: Numeric;
    duplaSkeniranja: {TrakaId: Numeric; stavkeIds: Numeric[]; koristenaStavkaId: Numeric}[];
    poredjenjeSaTrenutnimStanjemSkladista: {
        trenutnoStanjeSkladista: {brojTraka: Numeric; tezinaKg: Numeric};
        skeniranaPlusNeskeniranaTezinaKg: Numeric;
        razlikaKg: Numeric;
        trakeSaOdstupanjemOdTrenutnogStanja: TrenutnaTraka[];
    };
}
const validNumber = (value: unknown): boolean =>
    (typeof value === 'number' || (typeof value === 'string' && value.trim() !== '')) && Number.isFinite(Number(value));
const lengthValue = (value: Numeric | null | undefined) => validNumber(value) ? formatter.format(Number(value)) : 'Nema zapisa';
const closingPayload = (id: number, report: ReportData | null): ZakljuciInventuruTrakaPayload | null => {
    const values = {
        IdFromURL: id,
        stanjeLageraKG: report?.poredjenjeSaTrenutnimStanjemSkladista?.trenutnoStanjeSkladista?.tezinaKg,
        stanjeLageraKom: report?.poredjenjeSaTrenutnimStanjemSkladista?.trenutnoStanjeSkladista?.brojTraka,
        tezinaRolniPrilikomSkeniranja: report?.skeniraneTrake?.tezinaKg,
        tezinaRolniNakonSkeniranja: report?.evidentiranaTezinaSkeniranihTrakaKg,
        razlikaInventure: report?.razlikaSkeniraneIEvidentiraneTezineKg,
        rolneNisuPronadjeneKomada: report?.brojNeskeniranihTraka,
        rolneNisuPronadjeneKG: report?.tezinaNeskeniranihTrakaKg,
    };
    if (!Number.isInteger(id) || id <= 0 || !Object.values(values).every(validNumber)) return null;
    const neskenirane = report?.neskeniraneTrakeNaTrenutnomStanju;
    if (!neskenirane || typeof neskenirane !== 'object' || Array.isArray(neskenirane)) return null;
    const entries = Object.entries(neskenirane);
    if (!entries.every(([, stavka]) => stavka && typeof stavka === 'object' && validNumber(stavka.tezinaKg))) return null;
    const rolneNisuPronadjene = Object.fromEntries(entries.map(([trakaId, stavka]) =>
        [trakaId, Number((stavka as NeskeniranaTraka).tezinaKg)]));
    return {
        ...Object.fromEntries(Object.entries(values).map(([key, value]) => [key, Number(value)])),
        rolneNisuPronadjene,
    } as ZakljuciInventuruTrakaPayload;
};
const errorText = (error: any): string => {
    const body = error?.response?.data ?? error;
    const details = body?.errors ?? body?.data?.errors;
    const invalid = body?.data?.neispravneStavke ?? body?.neispravneStavke;
    return [body?.message || 'Greška prilikom učitavanja podataka.',
        details && JSON.stringify(details, null, 2),
        invalid && 'Neispravne stavke: ' + JSON.stringify(invalid, null, 2),
    ].filter(Boolean).join('\n');
};
const zadnjiPrometColumn: Column<ZadnjiPromet> = {
    title: 'Zadnji promet', render: row => row.uposlenik?.imePrezime || '—',
};
const weightColumns: Column<Traka>[] = [
    {title: 'ID trake', render: row => row.TrakaId},
    {title: 'Težina trake (kg)', render: row => kg(row.Tezina), totalValue: row => Number(row.Tezina)},
    {title: 'Skenirana težina (kg)', render: row => kg(row.SkeniranaTezina), totalValue: row => Number(row.SkeniranaTezina)},
];
const InventuraTrakaIzvjestaj: React.FC = () => {
    const {id} = useParams();
    const IdFromURL = Number(id);
    const [reportData, setReportData] = useState<ReportData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [relatedError, setRelatedError] = useState('');
    const [repairing, setRepairing] = useState(false);
    const [inventura, setInventura] = useState<Partial<InventureModel> | null>(null);
    const [inventoryLoading, setInventoryLoading] = useState(true);
    const [inventoryError, setInventoryError] = useState('');
    const [closing, setClosing] = useState(false);
    const [closingError, setClosingError] = useState('');
    const [closedId, setClosedId] = useState<number | null>(null);
    const closingLock = useRef(false);
    const inventoryRequest = useRef(0);
    const repairLock = useRef(false);
    const reportRequest = useRef(0);
    const locationRequest = useRef(0);
    const [lokacijaTrake, setLokacijaTrake] = useState('');
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [locationRows, setLocationRows] = useState<{TrakaId: Numeric}[]>([]);
    const [locationLoading, setLocationLoading] = useState(false);
    const [locationError, setLocationError] = useState('');

    const fetchInventory = useCallback(async () => {
        const request = ++inventoryRequest.current;
        setInventoryLoading(true);
        setInventura(null);
        setInventoryError('');
        try {
            if (!Number.isInteger(IdFromURL) || IdFromURL <= 0) throw new Error('Neispravan broj inventure.');
            const result = await fetchInventura(IdFromURL);
            if (request === inventoryRequest.current) setInventura(result);
        } catch (failure) {
            if (request === inventoryRequest.current) setInventoryError(errorText(failure));
        } finally {
            if (request === inventoryRequest.current) setInventoryLoading(false);
        }
    }, [IdFromURL]);
    useEffect(() => {
        setClosingError('');
        void fetchInventory();
        return () => {inventoryRequest.current++;};
    }, [fetchInventory]);

    const fetchReport = useCallback(async () => {
        const request = ++reportRequest.current;
        setLoading(true);
        setReportData(null);
        setError('');
        try {
            if (!Number.isInteger(IdFromURL) || IdFromURL <= 0) throw new Error('Neispravan broj inventure.');
            const response = await izvjestajZaInventuruTrakaV2({IdFromURL});
            const body = response.data;
            if ((body.error != null && Number(body.error) !== 0) || body.success === false ||
                !body.data?.skeniraneTrake || !body.data?.poredjenjeSaTrenutnimStanjemSkladista?.trenutnoStanjeSkladista) throw body;
            if (request === reportRequest.current) setReportData(body.data);
        } catch (failure) {
            if (request === reportRequest.current) setError(errorText(failure));
        } finally {
            if (request === reportRequest.current) setLoading(false);
        }
    }, [IdFromURL]);
    const fetchLocation = useCallback(async (location: string) => {
        const request = ++locationRequest.current;
        setLocationRows([]);
        setLocationError('');
        setLocationLoading(Boolean(location));
        if (!location) return;
        try {
            const response = await izvjestajPremaLokacijiTrake({lokacija: location, idInventure: IdFromURL});
            if (response?.error != null && Number(response.error) !== 0) throw response;
            const rows = Object.values(response ?? {}).filter(value =>
                (typeof value === 'number' || typeof value === 'string') && Number.isFinite(Number(value))
            ).map(value => ({TrakaId: value as Numeric}));
            if (request === locationRequest.current) setLocationRows(rows);
        } catch (failure) {
            if (request === locationRequest.current) setLocationError(errorText(failure));
        } finally {
            if (request === locationRequest.current) setLocationLoading(false);
        }
    }, [IdFromURL]);
    const fetchRelated = useCallback(async () => {
        setRelatedError('');
        try {
            const response = await getSumaTrakaInventurePremaFmkSifri({IdFromURL});
            if (response?.error != null && Number(response.error) !== 0) throw response;
        } catch (failure) {setRelatedError(errorText(failure));}
    }, [IdFromURL]);
    useEffect(() => {
        void fetchReport();
        if (Number.isInteger(IdFromURL) && IdFromURL > 0) void fetchRelated();
        return () => {reportRequest.current++;};
    }, [IdFromURL, fetchReport, fetchRelated]);
    useEffect(() => {
        void fetchLocation(lokacijaTrake);
        return () => {locationRequest.current++;};
    }, [fetchLocation, lokacijaTrake]);
    const refresh = async () => {
        await Promise.all([fetchReport(), fetchRelated(), fetchLocation(lokacijaTrake), fetchInventory()]);
    };
    const podesiNoveLokacije = async () => {
        if (repairLock.current || closingLock.current) return;
        repairLock.current = true;
        setRepairing(true);
        try {
            // Preserve the existing inventory-wide action and its exact parameters.
            const response = await podesiNoveLokacijeRolni({IdFromURL});
            if (response.error === 0) {
                notifyToast(true, response.message || 'Lokacije su uspješno ažurirane.');
                await refresh();
            } else notifyToast(false, response.message || 'Popravka lokacija nije uspjela.');
        } catch (failure) {notifyToast(false, errorText(failure));}
        finally {repairLock.current = false; setRepairing(false);}
    };
    const comparison = reportData?.poredjenjeSaTrenutnimStanjemSkladista;
    const payload = closingPayload(IdFromURL, reportData);
    const isClosed = closedId === IdFromURL || (Number(inventura?.Id) === IdFromURL && Number(inventura?.Status) === 37);
    const canClose = !isClosed && !loading && !inventoryLoading && !repairing && !closing && !!payload &&
        Number(inventura?.Id) === IdFromURL && Number(inventura?.Status) === 36 && !inventura?.Kraj;
    const zakljuciInventuru = async () => {
        if (!canClose || !payload || closingLock.current || repairLock.current) return;
        if (!window.confirm('Želite li zaključiti inventuru? Rezultati će biti sačuvani i status promijenjen u Zaključena.')) return;
        closingLock.current = true;
        setClosing(true);
        setClosingError('');
        try {
            const response = await zakljuciInventuruTraka(payload);
            if ((response?.error != null && Number(response.error) !== 0) || response?.success === false) throw response;
            setClosedId(IdFromURL);
            notifyToast(true, 'Inventura je uspješno zaključena');
            window.dispatchEvent(new Event(INVENTURE_UPDATED_EVENT));
            await fetchInventory();
        } catch (failure: any) {
            setClosingError(errorText(failure));
            if (failure?.response?.status === 409) {
                window.dispatchEvent(new Event(INVENTURE_UPDATED_EVENT));
                await fetchInventory();
            }
        } finally {
            closingLock.current = false;
            setClosing(false);
        }
    };
    return <>
        <ToastContainer />
        <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-6'>
            <h2>Inventura #{id} — izvještaj traka</h2>
            <div className='d-flex flex-wrap gap-3'>
                <button type='button' className='btn btn-light-primary' disabled={loading || repairing || closing || inventoryLoading || locationLoading}
                    onClick={() => void refresh()}>Osvježi</button>
                <button type='button' className='btn btn-primary' disabled={loading || repairing || closing || !reportData}
                    onClick={() => void podesiNoveLokacije()}>{repairing ? 'Popravka u toku…' : 'PODESI NOVE LOKACIJE'}</button>
                <button type='button' className='btn btn-success' disabled={!canClose}
                    onClick={() => void zakljuciInventuru()}>
                    {closing ? 'Zaključivanje…' : isClosed ? 'Inventura zaključena' : 'Zaključi inventuru'}
                </button>
            </div>
        </div>
        {inventura && <p>Status inventure: {Number(inventura.Status) === 37 ? 'Zaključena' : Number(inventura.Status) === 36 ? 'Aktivna' : inventura.Status}
            {inventura.Kraj && <> · Kraj: {inventura.Kraj}</>}</p>}
        {inventoryError && <div className='alert alert-danger' role='alert'>Status inventure nije učitan: {inventoryError}</div>}
        {closingError && <div className='alert alert-danger' role='alert' style={{whiteSpace: 'pre-wrap'}}>{closingError}</div>}
        {!loading && reportData && !payload && <p className='alert alert-warning'>Zaključivanje nije moguće jer nedostaju obavezni numerički podaci izvještaja.</p>}
        <p className='alert alert-info'>Težine i lokacije inventure sačuvane su prilikom skeniranja.
            Neskenirane trake koriste trenutno stanje skladišta.</p>
        {loading && <LoadingComponent />}
        {error && <div className='alert alert-danger' role='alert' style={{whiteSpace: 'pre-wrap'}}>{error}</div>}
        {relatedError && <div className='alert alert-warning' role='alert'>Povezani podaci po FMK šifri: {relatedError}</div>}
        {!loading && reportData && <>
            <div className='row'>
                <Metric title='Skenirane trake' value={<>{reportData.skeniraneTrake.brojTraka} <span className='fs-3'>kom</span></>} valueClassName='display-4 fw-bolder text-success' />
                <Metric title='Skenirana težina' value={kg(reportData.skeniraneTrake.tezinaKg)} tonnesValue={tonnes(reportData.skeniraneTrake.tezinaKg)}
                    description='Ukupna težina unesena tokom skeniranja traka na ovoj inventuri.' />
                <Metric title='Težina skeniranih traka' value={kg(reportData.evidentiranaTezinaSkeniranihTrakaKg)} tonnesValue={tonnes(reportData.evidentiranaTezinaSkeniranihTrakaKg)}
                    description='Trenutna težina trake u sistemu za vrijeme skeniranja. Prikazan je zbir za sve skenirane trake.' />
                <Metric title='Razlika inventure' value={kg(reportData.razlikaSkeniraneIEvidentiraneTezineKg)}
                    description='Skenirana težina − težina skeniranih traka u sistemu pri skeniranju. Razlika je prikazana u kilogramima (1 t = 1.000 kg). Pozitivna vrijednost znači da je uneseno više, a negativna da je uneseno manje od evidentirane težine.' />
                <Metric title='Neskenirane trake' value={<>{reportData.brojNeskeniranihTraka} <span className='fs-3'>kom</span></>} valueClassName='display-4 fw-bolder text-danger' />
                <Metric title='Težina neskeniranih' value={kg(reportData.tezinaNeskeniranihTrakaKg)} tonnesValue={tonnes(reportData.tezinaNeskeniranihTrakaKg)} />
            </div>
            <p>Pozitivna razlika znači da je skenirano više od evidentirane težine, a negativna da je skenirano manje.</p>
            <div className='card mb-6'><div className='card-body'>
                <Tabs id='inventura-trake-tabs' defaultActiveKey='neskenirane' mountOnEnter unmountOnExit>
                    <Tab eventKey='neskenirane' title='Neskenirane trake'>
                        <p className='mt-4'>Neskenirano ne znači automatski fizički manjak.</p>
                        <ReportTable<NeskeniranaTraka & {TrakaId: Numeric}> entity='trake' showTotals label='Neskenirane trake' rows={Object.entries(reportData.neskeniraneTrakeNaTrenutnomStanju ?? {})
                            .map(([TrakaId, value]) => typeof value === 'object'
                                ? {...value, TrakaId}
                                : {TrakaId, tezinaKg: value, uposlenik: null})} columns={[
                            {title: 'ID trake', render: row => row.TrakaId},
                            {title: 'Pakovanje', render: row => row.Pakovanje ?? '—'},
                            {title: 'Kreirana težina (kg)', render: row => kg(row.KreiranaTezina)},
                            {title: 'Trenutna težina (kg)', render: row => kg(row.tezinaKg), totalValue: row => Number(row.tezinaKg)},
                            {title: 'Lokacija', render: row => row.Lokacija ?? '—'},
                            zadnjiPrometColumn,
                        ]} />
                    </Tab>
                    <Tab eventKey='pronadjene' title='Pronađene trake (stanje bilo 0)'>
                        <p className='mt-4'>Evidentirana težina je 0, a skenirana veća od 0. Zbir: {kg(reportData.tezinaTrakaBezEvidentiraneTezineKg)}</p>
                        <ReportTable entity='trake' showTotals label='Pronađene bez evidentirane težine' rows={reportData.pronadjeneTrakeBezEvidentiraneTezine ?? []}
                            columns={[...weightColumns, {title: 'Skenirana lokacija', render: row => row.SkeniranaLokacija ?? '—'}, zadnjiPrometColumn]} />
                    </Tab>
                    <Tab eventKey='tezine' title='Greska u težini'>
                        <p className='mt-4'>Broj: {reportData.brojTrakaSaOdstupanjemTezine}. Ukupna razlika: {kg(reportData.razlikaTezineEvidentiranihTrakaKg)}</p>
                        <ReportTable entity='trake' showTotals label='Odstupanja težine' rows={reportData.trakeSaOdstupanjemTezine ?? []}
                            columns={[...weightColumns, {title: 'Razlika (kg)', render: row => kg(Number(row.SkeniranaTezina) - Number(row.Tezina)),
                                totalValue: row => Number(row.SkeniranaTezina) - Number(row.Tezina)}, zadnjiPrometColumn]} />
                    </Tab>
                    <Tab eventKey='duzine' title='Odstupanja dužine'>
                        <p className='mt-4'>Broj: {reportData.brojTrakaSaOdstupanjemDuzine}. Dužine su sačuvane prilikom skeniranja.
                            Razlika je skenirana dužina minus evidentirana dužina.</p>
                        <ReportTable<Traka> entity='trake' label='Odstupanja dužine' rows={reportData.trakeSaOdstupanjemDuzine ?? []} columns={[
                            {title: 'ID trake', render: row => row.TrakaId},
                            {title: 'Evidentirana dužina', render: row => lengthValue(row.Duzina)},
                            {title: 'Skenirana dužina', render: row => lengthValue(row.SkeniranaDuzina)},
                            {title: 'Razlika dužine', render: row => validNumber(row.Duzina) && validNumber(row.SkeniranaDuzina)
                                ? lengthValue(Number(row.SkeniranaDuzina) - Number(row.Duzina)) : 'Nema zapisa'},
                        ]} />
                    </Tab>
                    <Tab eventKey='lokacije' title='Odstupanja lokacije'>
                        <p className='mt-4'>Broj: {reportData.brojTrakaSaOdstupanjemLokacije}. Lokacija označava evidentiranu lokaciju prilikom skeniranja.
                            Sačuvane lokacije mogu ostati različite i nakon popravke trenutnog skladišta.</p>
                        <ReportTable entity='trake' label='Odstupanja lokacije' rows={reportData.trakeSaOdstupanjemLokacije ?? []} columns={[
                            {title: 'ID trake', render: row => row.TrakaId},
                            {title: 'Evidentirana lokacija', render: row => row.Lokacija ?? '—'},
                            {title: 'Skenirana lokacija', render: row => row.SkeniranaLokacija ?? '—'},
                            {title: 'Skenirani red lokacije', render: row => row.SkeniranaLokacijaRed ?? '—'},
                        ]} />
                    </Tab>
                    <Tab eventKey='dupla' title='Dupla skeniranja'>
                        <p className='mt-4'>U zbir se uključuje samo stavka s najvećim ID-em.</p>
                        <ReportTable entity='trake' label='Dupla skeniranja' rows={reportData.duplaSkeniranja ?? []} columns={[
                            {title: 'ID trake', render: row => row.TrakaId},
                            {title: 'ID-evi stavki', render: row => row.stavkeIds.join(', ')},
                            {title: 'Korištena stavka ID', render: row => row.koristenaStavkaId},
                        ]} />
                    </Tab>
                </Tabs>
            </div></div>
            {comparison && <section className='mb-6' aria-label='Poređenje s trenutnim skladištem'>
                <h3>Poređenje s trenutnim skladištem</h3>
                <p>Ova odstupanja predstavljaju poređenje s trenutnim stanjem skladišta i nisu greške težine iz inventure.
                    Razlika poredi skeniranu težinu s težinom uključenom u trenutno stanje.</p>
                <div className='row'>
                    <Metric title='Trenutno stanje — broj traka' value={<>{comparison.trenutnoStanjeSkladista.brojTraka} <span className='fs-3'>kom</span></>} valueClassName='display-4 fw-bolder text-success' />
                    <Metric title='Trenutno stanje — težina traka na lageru' value={kg(comparison.trenutnoStanjeSkladista.tezinaKg)} tonnesValue={tonnes(comparison.trenutnoStanjeSkladista.tezinaKg)} />
                    <Metric title='Skenirano + neskenirano' value={kg(comparison.skeniranaPlusNeskeniranaTezinaKg)} tonnesValue={tonnes(comparison.skeniranaPlusNeskeniranaTezinaKg)} />
                    <Metric title='Razlika prema trenutnom stanju' value={kg(comparison.razlikaKg)} />
                </div>
                <div className='card'><div className='card-body'>
                    <ReportTable<TrenutnaTraka> entity='trake' label='Odstupanja od trenutnog stanja' rows={comparison.trakeSaOdstupanjemOdTrenutnogStanja ?? []} columns={[
                        {title: 'ID trake', render: row => row.TrakaId},
                        {title: 'Pakovanje', render: row => row.Pakovanje ?? '—'},
                        {title: 'Evidentirano prilikom skeniranja (kg)', render: row => kg(row.Tezina)},
                        {title: 'Skenirana težina (kg)', render: row => kg(row.SkeniranaTezina)},
                        {title: 'Trenutna težina (kg)', render: row => row.trenutnaTezinaKg == null ? 'Nema zapisa' : kg(row.trenutnaTezinaKg)},
                    ]} />
                </div></div>
            </section>}
        </>}
        <section className='card mb-6'><div className='card-body'>
            <h3>Pregled po lokaciji — neskenirane trake</h3>
            <p className='text-muted'>Otvorite pregled i odaberite lokaciju za prikaz neskeniranih traka.</p>
            <button type='button' className='btn btn-light-primary' onClick={() => setShowLocationModal(true)}>
                Pregled po lokaciji
            </button>
        </div></section>
        <Modal show={showLocationModal} onHide={() => setShowLocationModal(false)} size='xl'
            fullscreen='md-down' centered scrollable aria-labelledby='location-report-title'>
            <Modal.Header closeButton closeLabel='Zatvori'>
                <Modal.Title id='location-report-title'>Inventura #{id} — pregled po lokaciji</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <h4>Odaberite lokaciju</h4>
                <LokacijeRsTraka value={lokacijaTrake} selectName='LokacijaTrake'
                    onChange={(_name, value) => setLokacijaTrake(String(value ?? ''))} />
                <p className='text-muted mt-4'>Neskenirane trake na odabranoj lokaciji. Neskenirano ne znači automatski fizički manjak.</p>
                {locationLoading ? <LoadingComponent /> : locationError ?
                    <div className='alert alert-danger mt-4' role='alert'>{locationError}</div> : lokacijaTrake ?
                    <div className='mt-4'><ReportTable entity='trake' key={lokacijaTrake} label='Neskenirane trake odabrane lokacije' rows={locationRows}
                        columns={[{title: 'ID trake', render: row => row.TrakaId}]} /></div> :
                    <p className='mt-4'>Odaberite lokaciju za pregled.</p>}
            </Modal.Body>
            <Modal.Footer>
                <button type='button' className='btn btn-light' onClick={() => setShowLocationModal(false)}>Zatvori</button>
            </Modal.Footer>
        </Modal>
    </>;
};
export default InventuraTrakaIzvjestaj;
